/**
 * CellsSection - Sección 2 del GridsPanel
 *
 * @version 1.0
 * @updated 2026-02-17
 * @module core/layouts/panels/LeftPanel
 *
 * @description
 * Renderiza la tabla de celdas de la malla activa en dos modos:
 * - Narrow (collapsed=false): Código + Total de elementos
 * - Wide   (collapsed=true):  Código + columna por tipo de elemento del módulo activo
 *
 * @remarks
 * Gestiona su propio estado de orden y paginación.
 * Los datos de elementos ambientales son mock (Phase 0).
 * Phase 1: conectar con API de inventario ambiental.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  TreeDeciduous, Trees, Umbrella, Droplets, Waves, Droplet,
  ArrowUp, ArrowDown, Search, BarChart3,
} from 'lucide-react';
import { useLeftPanel } from './LeftPanel';
import { TablePagination } from './TablePagination';
import type { SpatialGrid } from '../../../spatial';

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

export interface CellRow {
  h3Index: string;
  cellCode: string | null;
  /** Nombre personalizado asignado a la celda. Null si no tiene. */
  cellName: string | null;
  /** Conteo por tipo de elemento ambiental. Phase 0: vacío. */
  env: Record<string, number>;
}

type SortDir = 'asc' | 'desc';

// ─────────────────────────────────────────
// Constants
// ─────────────────────────────────────────

/** Altura aproximada de cada fila en px (min-height 22 + padding 4). */
const ROW_HEIGHT_PX = 26;

/**
 * Columnas del módulo ambiental activo.
 * Phase 1: estas columnas serán configurables por módulo.
 */
const ENV_COLUMNS = [
  { key: 'trees',       label: 'Árboles',                 icon: <TreeDeciduous size={12} /> },
  { key: 'parks',       label: 'Parques, Plazas y Jardines', icon: <Trees      size={12} /> },
  { key: 'sunshades',   label: 'Parasoles',               icon: <Umbrella   size={12} /> },
  { key: 'fountains',   label: 'Fuentes y Bebederos',     icon: <Droplets   size={12} /> },
  { key: 'waterCourses',label: 'Cursos de Agua',          icon: <Waves      size={12} /> },
  { key: 'waterMirrors',label: 'Espejos de Agua',         icon: <Droplet    size={12} /> },
] as const;

// ─────────────────────────────────────────
// Props
// ─────────────────────────────────────────

interface CellsSectionProps {
  activeGrid: SpatialGrid;
  cellRows: CellRow[];
  /** H3 index de la celda actualmente hover (desde mapa o tabla). */
  hoveredCellId: string | null;
  /** Callback cuando el usuario hace hover sobre una fila de la tabla. */
  onCellHover: (h3Index: string | null) => void;
  /** H3 index de la celda seleccionada (clic en mapa o tabla). */
  selectedCellId: string | null;
  /** Callback cuando el usuario selecciona/deselecciona una celda. */
  onCellSelect: (h3Index: string | null) => void;
  /** Vuela al centro de la celda en el mapa. */
  onZoomToCell: (h3Index: string) => void;
  /** Guarda el nombre personalizado de una celda (string vacío lo elimina). */
  onCellNameChange: (h3Index: string, name: string) => Promise<void>;
  /** Callback cuando se hace clic en el icono de estadísticas de una celda. */
  onCellStatsClick?: (h3Index: string) => void;
}

// ─────────────────────────────────────────
// Component
// ─────────────────────────────────────────

/**
 * Sección de celdas de la malla activa.
 *
 * @param activeGrid - Malla activa (para mostrar nombre y total)
 * @param cellRows   - Filas de celdas a mostrar
 */
export function CellsSection({ activeGrid, cellRows, hoveredCellId, onCellHover, selectedCellId, onCellSelect, onZoomToCell, onCellNameChange, onCellStatsClick }: CellsSectionProps): JSX.Element {
  const { collapsed } = useLeftPanel();

  // ── Sort state ────────────────────────────
  const [sortKey, setSortKey] = useState<string>('code');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // ── Inline cell name editing ───────────────
  const [editingCellId, setEditingCellId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  // ── Pagination state ──────────────────────
  const [page, setPage] = useState<number>(1);

  // ── Rows per page: medido dinámicamente ───
  /**
   * Ref callback: se llama cuando el div body monta/desmonta.
   * Permite observar el elemento sin un segundo useEffect.
   */
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const bodyRef = useCallback((el: HTMLDivElement | null) => setBodyEl(el), []);
  const [rowsPerPage, setRowsPerPage] = useState(15); // fallback hasta medir

  useEffect(() => {
    if (!bodyEl) return;
    const observer = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      if (h > 0) {
        setRowsPerPage(Math.max(1, Math.floor(h / ROW_HEIGHT_PX)));
      }
    });
    observer.observe(bodyEl);
    return () => observer.disconnect();
  }, [bodyEl]);

  /**
   * Cambia la columna de orden; invierte dirección si es la misma columna.
   */
  const handleSort = (key: string): void => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  // ── Sorted + paginated ────────────────────
  const sortedRows = useMemo(() => {
    return [...cellRows].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      if (sortKey === 'code') {
        aVal = a.cellCode ?? '';
        bVal = b.cellCode ?? '';
      } else if (sortKey === 'total') {
        aVal = Object.values(a.env).reduce((s, v) => s + v, 0);
        bVal = Object.values(b.env).reduce((s, v) => s + v, 0);
      } else {
        aVal = a.env[sortKey] ?? 0;
        bVal = b.env[sortKey] ?? 0;
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [cellRows, sortKey, sortDir]);

  // Ajustar página si el nuevo rowsPerPage la deja fuera de rango
  useEffect(() => {
    const max = Math.ceil(sortedRows.length / rowsPerPage) || 1;
    if (page > max) setPage(max);
  }, [rowsPerPage, sortedRows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(sortedRows.length / rowsPerPage);
  const pageRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  // ── Selección: saltar de página + scroll ──
  useEffect(() => {
    if (!selectedCellId) return;
    const idx = sortedRows.findIndex(r => r.h3Index === selectedCellId);
    if (idx === -1) return;
    const targetPage = Math.floor(idx / rowsPerPage) + 1;
    if (targetPage !== page) setPage(targetPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCellId]); // solo cuando cambia la selección

  useEffect(() => {
    if (!selectedCellId) return;
    // Espera a que React aplique el nuevo page en el DOM
    const t = setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-h3index="${selectedCellId}"]`);
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 30);
    return () => clearTimeout(t);
  }, [selectedCellId, page, collapsed]);

  // ── Sort indicator helper ─────────────────
  const sortIcon = (key: string): React.ReactNode => {
    if (sortKey !== key) return null;
    return sortDir === 'asc'
      ? <ArrowUp  size={9} className="gp-sort-icon" />
      : <ArrowDown size={9} className="gp-sort-icon" />;
  };

  // ── Render ────────────────────────────────
  return (
    <section className="gp-section gp-cells-section" aria-label="Celdas de la malla activa">

      {/* Header de sección */}
      <header className="gp-section-header">
        <span className="gp-section-title">Celdas</span>
        <span className="gp-section-grid-name">{activeGrid.name}</span>
        <span className="gp-section-count">{cellRows.length.toLocaleString()}</span>
      </header>

      {/* ── Narrow: Código + Total + Stats ── */}
      {!collapsed && (
        <>
          <div className="gp-cells-header gp-cells-header--narrow">
            <button
              className={`gp-col-header ${sortKey === 'code' ? 'active' : ''}`}
              onClick={() => handleSort('code')}
              title="Ordenar por código"
            >
              Código {sortIcon('code')}
            </button>
            <button
              className={`gp-col-header gp-col-header--right ${sortKey === 'total' ? 'active' : ''}`}
              onClick={() => handleSort('total')}
              title="Ordenar por total de elementos"
            >
              Total {sortIcon('total')}
            </button>
            <div className="gp-col-header gp-col-header--icon" title="Estadísticas">
              <BarChart3 size={12} />
            </div>
          </div>

          <div className="gp-cells-body" ref={bodyRef}>
            {pageRows.map(cell => {
              const total = Object.values(cell.env).reduce((s, v) => s + v, 0);
              const isHovered  = cell.h3Index === hoveredCellId;
              const isSelected = cell.h3Index === selectedCellId;
              return (
                <div
                  key={cell.h3Index}
                  data-h3index={cell.h3Index}
                  className={`gp-cell-row gp-cell-row--narrow${isHovered ? ' hovered' : ''}${isSelected ? ' selected' : ''}`}
                  onMouseEnter={() => onCellHover(cell.h3Index)}
                  onMouseLeave={() => onCellHover(null)}
                  onClick={() => onCellSelect(isSelected ? null : cell.h3Index)}
                >
                  <div className="gp-cell-code-cell">
                    <button
                      className="gp-zoom-btn"
                      onClick={e => { e.stopPropagation(); onZoomToCell(cell.h3Index); }}
                      title="Acercarse en el mapa"
                    >
                      <Search size={9} />
                    </button>
                    <span className="gp-cell-code">{cell.cellCode ?? '—'}</span>
                    {editingCellId === cell.h3Index ? (
                      <input
                        className="gp-cell-name-input"
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onBlur={() => {
                          void onCellNameChange(cell.h3Index, editingName);
                          setEditingCellId(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            void onCellNameChange(cell.h3Index, editingName);
                            setEditingCellId(null);
                          }
                          if (e.key === 'Escape') setEditingCellId(null);
                          e.stopPropagation();
                        }}
                        onClick={e => e.stopPropagation()}
                        autoFocus
                        placeholder="Nombre…"
                        maxLength={100}
                      />
                    ) : (
                      <span
                        className="gp-cell-name"
                        onClick={e => {
                          e.stopPropagation();
                          setEditingCellId(cell.h3Index);
                          setEditingName(cell.cellName ?? '');
                        }}
                        title={cell.cellName ? cell.cellName : 'Agregar nombre'}
                      >
                        {cell.cellName ?? <span className="gp-cell-name-empty">+</span>}
                      </span>
                    )}
                  </div>
                  <span className="gp-cell-total">{total}</span>
                  {onCellStatsClick && (
                    <button
                      className="gp-cell-stats-btn"
                      onClick={e => {
                        e.stopPropagation();
                        onCellStatsClick(cell.h3Index);
                      }}
                      title="Ver estadísticas de la celda"
                    >
                      <BarChart3 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Wide: Código + columnas ambientales ── */}
      {collapsed && (
        <>
          <div className="gp-cells-header gp-cells-header--wide">
            <button
              className={`gp-col-header ${sortKey === 'code' ? 'active' : ''}`}
              onClick={() => handleSort('code')}
              title="Ordenar por código"
            >
              Cód {sortIcon('code')}
            </button>
            {ENV_COLUMNS.map(col => (
              <button
                key={col.key}
                className={`gp-col-header gp-col-header--icon ${sortKey === col.key ? 'active' : ''}`}
                onClick={() => handleSort(col.key)}
                title={`${col.label} — click para ordenar`}
                aria-label={col.label}
              >
                {col.icon}
                {sortIcon(col.key)}
              </button>
            ))}
          </div>

          <div className="gp-cells-body" ref={bodyRef}>
            {pageRows.map(cell => {
              const isHovered  = cell.h3Index === hoveredCellId;
              const isSelected = cell.h3Index === selectedCellId;
              return (
                <div
                  key={cell.h3Index}
                  data-h3index={cell.h3Index}
                  className={`gp-cell-row gp-cell-row--wide${isHovered ? ' hovered' : ''}${isSelected ? ' selected' : ''}`}
                  onMouseEnter={() => onCellHover(cell.h3Index)}
                  onMouseLeave={() => onCellHover(null)}
                  onClick={() => onCellSelect(isSelected ? null : cell.h3Index)}
                >
                  <div className="gp-cell-code-cell">
                    <button
                      className="gp-zoom-btn"
                      onClick={e => { e.stopPropagation(); onZoomToCell(cell.h3Index); }}
                      title="Acercarse en el mapa"
                    >
                      <Search size={9} />
                    </button>
                    <span className="gp-cell-code" title={cell.cellName ?? undefined}>{cell.cellCode ?? '—'}</span>
                  </div>
                  {ENV_COLUMNS.map(col => (
                    <span key={col.key} className="gp-cell-env-count">
                      {cell.env[col.key] ?? 0}
                    </span>
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <TablePagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </section>
  );
}
